import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from "axios";
import { hideLoader, showLoader } from "../../utils/loader";
import toast from "react-hot-toast";
import { defaultErrorMessages, getAPIBaseUrl } from "../../utils";
import { refreshToken } from "services/authService";
import { formatApiErrorDetail } from "../../utils/formatApiErrorDetail";

interface FailedQueueItem {
    resolve: (token: string) => void;
    reject: (error: any) => void;
}

interface RefreshTokenResponse {
    success: boolean;
    data: {
        access_token: string;
        refresh_token: string;
    } | null;
    error: any;
    status?: number;
}

interface ErrorResponse {
    detail?: unknown;
}

const getUrl = getAPIBaseUrl();
const AUTH_STORAGE_KEYS = ["token", "refresh_token"] as const;

const clearAuthTokens = (): void => {
    AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
};

const getRefreshFailureStatus = (refreshError: any): number | undefined => {
    return refreshError?.response?.status ?? refreshError?.status;
};

const shouldLogoutOnRefreshFailure = (refreshStatus?: number): boolean => {
    return refreshStatus === 401;
};

const dispatchAuthLogout = (reason: string): void => {
    window.dispatchEvent(new CustomEvent("auth:logout", { detail: { reason } }));
};
// Simple queue for handling concurrent refresh requests
let isRefreshing = false;
let failedQueue: FailedQueueItem[] = [];
let lastOfflineToastAt = 0;

type RetriableRequestConfig = AxiosRequestConfig & { _retry?: boolean; suppressErrorToast?: boolean };

const isExpectedReconciliationAuthorizationError = (status: number | undefined, url?: string): boolean =>
    status === 403 && Boolean(url?.includes('audit-reports/reconciliation-summary'));

const isExpectedNotFoundError = (status: number | undefined, url?: string): boolean =>
    status === 404 && Boolean(url && (
        url.includes('logo-image') ||
        url.includes('audit-by-date') ||
        url.includes('audit-reconciliation/stores/') ||
        url.includes('audit-reports') ||
        url.includes('lottery/manual-day-entry') ||
        url.includes('receipt-audits/') ||
        url.includes('/sales/shift-summary')
    ));

const showOfflineToast = (): void => {
    const now = Date.now();
    if (now - lastOfflineToastAt < 5000) {
        return;
    }

    lastOfflineToastAt = now;
    toast.error("No internet connection. Please check your network and try again.");
};

// Enhanced offline detection function
const isUserOffline = (error?: AxiosError): boolean => {
    if (error?.response) {
        return false;
    }

    const navigatorOnline = navigator.onLine;
    const errorWithOffline = error as (AxiosError & { isOffline?: boolean }) | undefined;
    const isNetworkError = error?.code === 'ERR_NETWORK' || error?.code === 'ERR_INTERNET_DISCONNECTED';

    return !navigatorOnline || isNetworkError || Boolean(errorWithOffline?.isOffline);
};

// Check if user is offline (without error context)
const checkUserOffline = (): boolean => {
    return !navigator.onLine;
};


const processQueue = (error: any, token: string | null = null): void => {
    failedQueue.forEach(prom => {
        if (error) prom.reject(error);
        else prom.resolve(token!);
    });
    failedQueue = [];
};

// Function to handle online event and trigger refresh token
const handleOnlineEvent = async (): Promise<void> => {
    if (failedQueue.length > 0) {
        
        try {
            const refresh_token = localStorage.getItem("refresh_token");
            if (!refresh_token) {
                console.error("❌ No refresh token found in localStorage");
                processQueue(new Error("No refresh token available"), null);
                return;
            }

            const res: RefreshTokenResponse = await refreshToken(refresh_token);

            if (res.success && res.data) {
                const newToken = res.data.access_token;
                if (newToken) {
                    localStorage.setItem("token", newToken);
                    localStorage.setItem("refresh_token", res.data.refresh_token);
                    processQueue(null, newToken);
                    console.log("✅ Token refreshed successfully after coming online");
                } else {
                    throw new Error("No access token received");
                }
            } else {
                const refreshFailure = new Error("Token refresh failed") as any;
                refreshFailure.status = res.status;
                refreshFailure.response = { status: res.status };
                refreshFailure.message = typeof res.error === "string" ? res.error : "Token refresh failed";
                refreshFailure.data = res.error;
                throw refreshFailure;
            }
        } catch (refreshError: any) {
            console.error("❌ Failed to refresh token after coming online:", refreshError);
            processQueue(refreshError, null);
            const refreshStatus = getRefreshFailureStatus(refreshError);
            if (shouldLogoutOnRefreshFailure(refreshStatus)) {
                clearAuthTokens();
                dispatchAuthLogout("token_refresh_401");
            }
        }
    }
};

// Add event listener for online event
window.addEventListener('online', handleOnlineEvent);

const Api: AxiosInstance = axios.create({
    baseURL: getUrl || "",
    timeout: 60000, // Add timeout
});

// Request interceptor with debugging
Api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    // navigator.onLine is not reliable enough to block requests pre-flight.
    // Let the request proceed and handle real network failures in the response error path.
    if (checkUserOffline()) {
        console.log("📴 Browser reported offline before API call:", config.url);
    }
    
    const token = localStorage.getItem('token');
    if (token && config.headers) {
        config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
}, (error) => {
    console.error("❌ Request Error:", error);
    return Promise.reject(error);
});

// Response interceptor - handle 401 and refresh token
Api.interceptors.response.use(
    (response: AxiosResponse) => {
        return response;
    },
    async (error: AxiosError) => {
        const originalRequest = (error.config ?? null) as RetriableRequestConfig | null;
      
        const status = error?.response?.status;

        // Skip toast notification for expected optional/missing resources.
        if (isExpectedNotFoundError(status, originalRequest?.url)) {
            console.log("🚨 404 Error on  API call - skipping toast notification");
            return Promise.reject(error);
        }

        if (isExpectedReconciliationAuthorizationError(status, originalRequest?.url)) {
            return Promise.reject(error);
        }

        // Handle 401 errors and refresh token logic
        if ((status === 401 && !originalRequest?._retry) || error.code === 'ERR_NETWORK') {
            // Enhanced offline detection with debugging
            const errorWithOffline = error as AxiosError & { isOffline?: boolean };
            const hasHttpResponse = Boolean(error.response);
            const browserReportedOffline = checkUserOffline();
            const userIsOffline = isUserOffline(error);
            
            console.log("🚨 401/Network Error Details:", {
                status,
                errorCode: error.code,
                hasHttpResponse,
                browserReportedOffline,
                errorFlaggedOffline: Boolean(errorWithOffline.isOffline),
                userIsOffline,
                isRefreshing,
                failedQueueLength: failedQueue.length,
                originalRequestUrl: originalRequest?.url,
                timestamp: new Date().toISOString()
            });

            // Only queue when the request truly failed due to no connectivity.
            // A real 401 response means the server was reachable, so token refresh should run.
            if (!hasHttpResponse && userIsOffline) {
                console.log("📴 User is offline or network error detected - queueing request without refresh token call");

                showOfflineToast();

                if (!originalRequest) {
                    console.warn("⚠️ Network error received without Axios request config; cannot queue retry.");
                    return Promise.reject(error);
                }
                
                // Queue the request but don't call refresh token API
                return new Promise<string>((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(token => {
                    if (originalRequest.headers) {
                        originalRequest.headers["Authorization"] = `Bearer ${token}`;
                    }
                    return Api.request(originalRequest);
                }).catch(err => {
                    console.error("❌ Queued request failed:", err);
                    return Promise.reject(err);
                });
            }

            if (!originalRequest) {
                console.warn("⚠️ Auth/network error received without Axios request config; cannot refresh and replay request.");
                return Promise.reject(error);
            }

            if (isRefreshing) {
                return new Promise<string>((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(token => {
                    if (originalRequest.headers) {
                        originalRequest.headers["Authorization"] = `Bearer ${token}`;
                    }
                    return Api.request(originalRequest);
                }).catch(err => {
                    console.error("❌ Queued request failed:", err);
                    return Promise.reject(err);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            showLoader();

            try {
                const refresh_token = localStorage.getItem("refresh_token");
                if (!refresh_token) {
                    console.error("❌ No refresh token found in localStorage");
                    throw new Error("No refresh token available");
                }

                const res: RefreshTokenResponse = await refreshToken(refresh_token);
                console.log("🔄 Refresh Token Response:", res);

                // Check if the response was successful and has data
                if (res.success && res.data) {
                    const newToken = res.data.access_token;

                    if (newToken) {
                        localStorage.setItem("token", newToken);
                        localStorage.setItem("refresh_token", res.data.refresh_token);

                        if (originalRequest.headers) {
                            originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
                        }
                        processQueue(null, newToken);

                        return Api.request(originalRequest);
                    } else {
                        throw new Error("No access token received");
                    }
                } else {
                    const refreshFailure = new Error("Token refresh failed") as any;
                    refreshFailure.status = res.status;
                    refreshFailure.response = { status: res.status };
                    refreshFailure.message = typeof res.error === "string" ? res.error : "Token refresh failed";
                    refreshFailure.data = res.error;
                    throw refreshFailure;
                }
            } catch (refreshError: any) {
                const refreshStatus = getRefreshFailureStatus(refreshError);
                
                // Skip toast notification for expected optional/missing resources.
                if (isExpectedNotFoundError(refreshStatus, originalRequest?.url)) {
                    console.log("Error is 404 and the url is", originalRequest?.url);
                    console.log("🚨 404 Error on API call (refresh token flow) - skipping toast notification");
                    processQueue(refreshError, null);
                    return Promise.reject(error);
                }
                
                let errorMessage = "Authentication failed. Please log in again.";

                // Handle specific refresh token error cases
                if (refreshError.message?.includes('CORS') || refreshError.code === 'ERR_NETWORK') {
                    errorMessage = "CORS error during authentication. Please check your API configuration.";
                } else if (refreshError.code === 'ECONNABORTED') {
                    errorMessage = "Authentication timeout. Please try again.";
                } else if (!refreshError.response && !refreshError.request) {
                    errorMessage = "Network error during authentication. Please check your internet connection.";
                } else if (refreshStatus === 401) {
                    errorMessage = "Session expired. Please log in again.";
                } else if (refreshStatus === 403 || refreshStatus === 400) {
                    errorMessage = "Authentication refresh failed. Please try again.";
                } else if (refreshStatus === 500) {
                    errorMessage = "Server error during authentication. Please try again.";
                } else if (refreshError?.response?.data && typeof refreshError.response.data === 'object' && 'detail' in refreshError.response.data) {
                    errorMessage = formatApiErrorDetail(
                        (refreshError.response.data as ErrorResponse).detail,
                        errorMessage,
                    );
                } else {
                    errorMessage = refreshStatus !== undefined ? (defaultErrorMessages[refreshStatus] || errorMessage) : errorMessage;
                }

                toast.error(errorMessage);
                processQueue(refreshError, null);
                if (shouldLogoutOnRefreshFailure(refreshStatus)) {
                    clearAuthTokens();
                    dispatchAuthLogout("token_refresh_401");
                }
                // Return the original error with 401 status, not the refresh error
                return Promise.reject(error);
            } finally {
                isRefreshing = false;
                hideLoader();
            }
        }

        // Do not clear tokens for generic API 401s; only refresh-token 401 should end the session.
        if (status === 401) {
            console.log("🚪 401 Unauthorized - returning error without clearing tokens");
            return Promise.reject(error);
        }

        // Check for offline status on any API error
        const userIsOffline = isUserOffline(error);
        if (userIsOffline) {
            console.log("📴 User is offline during API call");
            showOfflineToast();
            return Promise.reject(error);
        }

        if (originalRequest?.suppressErrorToast) {
            return Promise.reject(error);
        }

        // Enhanced error handling for other status codes
        if (status) {
            if (error.response?.data && typeof error.response.data === 'object' && 'detail' in error.response.data) {
                const detailMessage = formatApiErrorDetail((error.response.data as ErrorResponse).detail);
                console.error(`❌ API Error ${status}:`, detailMessage);
                toast.error(detailMessage);
            } else {
                const fallbackMessage = defaultErrorMessages[status] || "Something went wrong. Please try again.";
                console.error(`❌ API Error ${status}:`, fallbackMessage);
                toast.error(fallbackMessage);
            }
        } else {
            // Handle errors without status code
            console.error("❌ Unknown API Error:", error.message);
            toast.error("An unexpected error occurred. Please try again.");
        }

        return Promise.reject(error);
    }
);

export default Api;

// Health check function to test API connectivity
export const checkApiHealth = async (): Promise<boolean> => {
    try {
        console.log("🏥 Checking API health...");
        const response = await Api.get('/v1/auth/me', { timeout: 5000 });
        console.log("✅ API health check successful:", response.status);
        return true;
    } catch (error: any) {
        console.error("❌ API health check failed:", {
            message: error.message,
            code: error.code,
            status: error?.response?.status,
            isNetworkError: !error.response && !error.request,
            isCorsError: error.message?.includes('CORS') || error.code === 'ERR_NETWORK'
        });
        return false;
    }
}; 
